
import { useState } from 'react';

export default function Reviews() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    service: '',
    rating: 0,
    review: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.rating === 0) return;
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const body = new URLSearchParams();
      body.append('name', formData.name);
      body.append('email', formData.email);
      body.append('service', formData.service);
      body.append('rating', String(formData.rating));
      body.append('review', formData.review);

      const response = await fetch('https://readdy.ai/api/form/d6audrnmvg9ih2c7arng', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', service: '', rating: 0, review: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="reviews" className="py-16 sm:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 rounded-full mb-4 sm:mb-6">
            <i className="ri-star-fill text-teal-600 text-lg"></i>
            <span className="text-teal-700 font-semibold text-sm">Customer Reviews</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            What Our Customers Say
          </h2>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
            Read reviews from real customers or share your own experience with Parker Family Tech
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          <div className="bg-gradient-to-br from-teal-50 to-white rounded-2xl p-6 sm:p-8 md:p-10 shadow-lg border border-teal-100">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6 text-center">
              Leave a Review
            </h3>
            
            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-green-600 text-xl mt-0.5 flex-shrink-0"></i>
                <div>
                  <p className="font-semibold text-green-900 text-sm">Thank you for your review!</p>
                  <p className="text-green-700 text-sm mt-1">Your feedback helps us improve our services.</p>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <i className="ri-error-warning-fill text-red-600 text-xl mt-0.5 flex-shrink-0"></i>
                <div>
                  <p className="font-semibold text-red-900 text-sm">Submission failed</p>
                  <p className="text-red-700 text-sm mt-1">Please try again later.</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6" data-readdy-form id="customer-review-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="service" className="block text-sm font-semibold text-gray-700 mb-2">
                  Service Used *
                </label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all cursor-pointer"
                >
                  <option value="">Select a service</option>
                  <option value="PC Repair">PC Repair</option>
                  <option value="Chromebook Repair">Chromebook Repair</option>
                  <option value="Custom PC Build">Custom PC Build</option>
                  <option value="Software Services">Software Services</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Rating *
                </label>
                <div className="flex gap-1 sm:gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setFormData({ ...formData, rating: star })}
                      className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center transition-all hover:scale-110"
                    >
                      <i
                        className={`text-2xl sm:text-3xl ${
                          star <= formData.rating
                            ? 'ri-star-fill text-yellow-400'
                            : 'ri-star-line text-gray-300'
                        }`}
                      ></i>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label htmlFor="review" className="block text-sm font-semibold text-gray-700 mb-2">
                  Your Review *
                </label>
                <textarea
                  id="review"
                  name="review"
                  value={formData.review}
                  onChange={handleInputChange}
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all resize-none"
                  placeholder="Share your experience with Parker Family Tech..."
                ></textarea>
                <p className="text-xs text-gray-500 mt-2 text-right">
                  {formData.review.length}/500 characters
                </p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting || formData.rating === 0}
                className="w-full bg-teal-600 text-white font-bold py-3.5 sm:py-4 px-6 rounded-lg hover:bg-teal-700 transition-all duration-300 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-lg"></i>
                    Submitting...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill text-lg"></i>
                    Submit Review
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
